import { canonicalDocument, persistableSnapshot } from "./board-document";
import { acceptGraph, type CanvasGraph, type GraphCommand } from "./canvas-commands";

export const HISTORY_LIMIT = 100;
export type EditToken = { epoch: number; id: number };
export type HistoryEntry = { before: CanvasGraph; after: CanvasGraph };
export type CanvasHistory = {
  graph: CanvasGraph;
  past: HistoryEntry[];
  future: HistoryEntry[];
  session: { token: EditToken; before: CanvasGraph; kind: string; changed: boolean } | null;
  epoch: number;
  nextToken: number;
};

export function authoredGraph(graph: CanvasGraph): CanvasGraph {
  const { nodes, edges } = persistableSnapshot({ format: "reactflow", version: 1, nodes: graph.nodes, edges: graph.edges });
  return { nodes, edges };
}
export function sameGraph(a: CanvasGraph, b: CanvasGraph) {
  const canonical = (graph: CanvasGraph) => canonicalDocument({ title: "", snapshot: { format: "reactflow", version: 1, nodes: graph.nodes, edges: graph.edges } });
  return canonical(a) === canonical(b);
}
export function createHistory(graph: CanvasGraph = { nodes: [], edges: [] }, epoch = 0): CanvasHistory {
  return { graph: structuredClone(graph), past: [], future: [], session: null, epoch, nextToken: 0 };
}
export function hasSession(state: CanvasHistory, token: EditToken | null): boolean {
  return !!token && state.epoch === token.epoch && state.session?.token.id === token.id;
}
function record(state: CanvasHistory, before: CanvasGraph): CanvasHistory {
  if (sameGraph(before, state.graph)) return state;
  return { ...state, past: [...state.past, { before: authoredGraph(before), after: authoredGraph(state.graph) }].slice(-HISTORY_LIMIT), future: [] };
}
export function finishSession(state: CanvasHistory, token: EditToken | null = state.session?.token ?? null): CanvasHistory {
  if (!hasSession(state, token)) return state;
  return record({ ...state, session: null }, state.session!.before);
}
export function beginSession(state: CanvasHistory, kind: string): CanvasHistory {
  const next = finishSession(state);
  const token = { epoch: next.epoch, id: next.nextToken + 1 };
  return { ...next, nextToken: token.id, session: { token, kind, before: authoredGraph(next.graph), changed: false } };
}
export function executeCommand(state: CanvasHistory, command: GraphCommand, token?: EditToken): CanvasHistory {
  if (token && !hasSession(state, token)) return state;
  const base = token ? state : finishSession(state);
  // Commands never receive retained history operands or mutate the live input.
  const graph = acceptGraph(base.graph, command(base.graph));
  const next = { ...base, graph };
  return token ? { ...next, session: { ...base.session!, changed: !sameGraph(graph, base.session!.before) } } : record(next, base.graph);
}
export function updateView(state: CanvasHistory, update: GraphCommand): CanvasHistory {
  const graph = update(state.graph);
  if (graph === state.graph) return state;
  return sameGraph(graph, state.graph) ? { ...state, graph } : state;
}
function replayGraph(graph: CanvasGraph, current: CanvasGraph): CanvasGraph {
  const result = authoredGraph(graph);
  const nodes = new Map(current.nodes.map(node => [node.id, node]));
  const edges = new Map(current.edges.map(edge => [edge.id, edge]));
  return { nodes: result.nodes.map(node => ({ ...node, selected: nodes.get(node.id)?.selected ?? false })),
    edges: result.edges.map(edge => ({ ...edge, selected: edges.get(edge.id)?.selected ?? false })) };
}
function clearStaleHistory(state: CanvasHistory): CanvasHistory {
  return { ...state, past: [], future: [], session: null, epoch: state.epoch + 1 };
}
export function undoHistory(state: CanvasHistory): CanvasHistory {
  const base = finishSession(state), entry = base.past[base.past.length - 1];
  if (!entry) return base;
  if (!sameGraph(base.graph, entry.after)) return clearStaleHistory(base);
  return { ...base, graph: replayGraph(entry.before, base.graph), past: base.past.slice(0, -1),
    future: [...base.future, entry], epoch: base.epoch + 1 };
}
export function redoHistory(state: CanvasHistory): CanvasHistory {
  const base = finishSession(state), entry = base.future[base.future.length - 1];
  if (!entry) return base;
  if (!sameGraph(base.graph, entry.before)) return clearStaleHistory(base);
  return { ...base, graph: replayGraph(entry.after, base.graph), past: [...base.past, entry],
    future: base.future.slice(0, -1), epoch: base.epoch + 1 };
}
export function closeHistoryAuthority(state: CanvasHistory): CanvasHistory {
  return { ...finishSession(state), epoch: state.epoch + 1 };
}
export function resetHistory(state: CanvasHistory, graph: CanvasGraph): CanvasHistory {
  return createHistory(graph, state.epoch + 1);
}
